'use client';

import Link from 'next/link';
import { User } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { Reveal } from '@/components/public/reveal';

interface TeamMemberCard {
  id: string;
  name: string;
  title: string;
  bio: string;
  imageUrl?: string | null;
}

function EducatorCard({ educator, featured = false }: { educator: TeamMemberCard; featured?: boolean }) {
  const initials = educator.name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <div
      className={`h-full rounded-xl border bg-white p-6 text-center transition-all duration-300 hover:-translate-y-1 hover:shadow-lg ${
        featured ? 'sm:p-8' : ''
      }`}
    >
      {educator.imageUrl ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={educator.imageUrl}
          alt={educator.name}
          className={`mx-auto mb-4 rounded-full object-cover ${featured ? 'h-24 w-24' : 'h-20 w-20'}`}
        />
      ) : (
        <div
          className={`mx-auto mb-4 flex items-center justify-center rounded-full bg-eifo-primary/10 ${
            featured ? 'h-24 w-24' : 'h-20 w-20'
          }`}
        >
          <span className={`font-bold text-eifo-primary ${featured ? 'text-2xl' : 'text-lg'}`}>
            {initials}
          </span>
        </div>
      )}
      <h3 className={`font-semibold text-eifo-primary ${featured ? 'text-xl' : 'text-lg'}`}>
        {educator.name}
      </h3>
      <p className="mb-3 text-sm text-eifo-orange">{educator.title}</p>
      <p className="text-sm text-gray-600">{educator.bio}</p>
    </div>
  );
}

export function EducatorsContent({
  founders,
  teachers,
}: {
  founders: TeamMemberCard[];
  teachers: TeamMemberCard[];
}) {
  const t = useTranslations('public.educators');

  return (
    <>
      {/* Header */}
      <section className="relative overflow-hidden bg-[var(--eifo-bg-light)] py-16 md:py-24">
        <div
          aria-hidden
          className="pointer-events-none absolute -left-20 -top-20 h-72 w-72 rounded-full bg-eifo-accent/20 blur-3xl"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-24 -right-16 h-72 w-72 rounded-full bg-eifo-orange/10 blur-3xl"
        />
        <div className="relative mx-auto max-w-3xl px-4 text-center">
          <h1 className="font-display text-4xl font-bold text-eifo-primary md:text-5xl">
            {t('heroTitle')}
          </h1>
          <p className="mt-6 text-lg text-gray-600">{t('heroSubtitle')}</p>
        </div>
      </section>

      {/* Founders */}
      {founders.length > 0 && (
        <section className="bg-white py-16">
          <div className="mx-auto max-w-4xl px-4">
            <h2 className="font-display mb-8 text-center text-2xl font-bold text-eifo-primary">
              {t('foundersHeading')}
            </h2>
            <div className="mx-auto grid max-w-2xl gap-8 sm:grid-cols-2">
              {founders.map((educator, i) => (
                <Reveal key={educator.id} delayMs={(i % 2) * 100}>
                  <EducatorCard educator={educator} featured />
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Teachers */}
      <section className="bg-[var(--eifo-bg-light)] py-16">
        <div className="mx-auto max-w-7xl px-4">
          <h2 className="font-display mb-8 text-center text-2xl font-bold text-eifo-primary">
            {t('teachersHeading')}
          </h2>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {teachers.map((educator, i) => (
              <Reveal key={educator.id} delayMs={(i % 3) * 90}>
                <EducatorCard educator={educator} />
              </Reveal>
            ))}
          </div>
          <p className="mt-10 text-center text-lg font-medium text-gray-500">{t('andManyMore')}</p>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-white py-16">
        <div className="mx-auto max-w-3xl px-4 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-eifo-orange/10">
            <User className="h-6 w-6 text-eifo-orange" />
          </div>
          <h2 className="font-display mb-4 text-2xl font-bold text-eifo-primary">{t('joinHeading')}</h2>
          <p className="mb-6 text-gray-600">{t('joinBody')}</p>
          <Link
            href="/contact?inquiry=teacher"
            className="inline-block rounded-md bg-eifo-orange px-6 py-3 text-sm font-medium text-white hover:bg-eifo-orange/90"
          >
            {t('joinButton')}
          </Link>
        </div>
      </section>
    </>
  );
}
